/**
 * Goods coming back, on a posted entry (migration 0088; the inventory plan's
 * §15.10, step I2b). Two forms in one place, chosen by what the entry is:
 *
 * - on a SALE whose cost is recorded, Record Returned Goods brings goods a
 *   customer gave back into the pool they left, at the cost they left at
 *   (§15.5). The money is entered as it is today: a refund on a bank row, or
 *   an entry against what the customer owes;
 * - on a BILL that brought goods in, Send Goods Back returns some to the
 *   vendor, out of that bill's own goods, as a credit against it (§15.4).
 *
 * Each writes and posts an entry of its own, so this entry is not changed.
 * The Matching screen's inline pattern: a button, then a short form in place.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api, type ReturnableGoods } from '../api.js';
import { showQuantity } from '../pages/ItemsPage.js';
import { InfoNote } from './InfoNote.js';
import { Toast } from './Toast.js';

/** Today, in the browser's own calendar, as a date input holds it. */
export function localToday(): string {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

const QUANTITY = /^\d{1,14}(\.\d{1,4})?$/;

export function GoodsReturnForm({ entryId }: { entryId: string }) {
  const [returnable, setReturnable] = useState<ReturnableGoods | null>(null);
  const [open, setOpen] = useState(false);
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [date, setDate] = useState(localToday);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [recorded, setRecorded] = useState<string | null>(null);

  const load = useCallback(() => {
    let current = true;
    api
      .returnableGoods(entryId)
      .then((next) => current && setReturnable(next))
      // Nothing to offer is the quiet answer here; the entry itself still reads and saves.
      .catch(() => current && setReturnable(null));
    return () => {
      current = false;
    };
  }, [entryId]);
  useEffect(load, [load]);

  if (returnable === null || returnable.kind === null) return null;
  const sale = returnable.kind === 'sale';
  const open_ = returnable.lines.filter((line) => Number(line.left) > 0);
  if (open_.length === 0 && recorded === null) return null;

  async function submit() {
    const wanted = open_
      .map((line) => ({ line, quantity: (amounts[line.lineId] ?? '').trim() }))
      .filter(({ quantity }) => quantity !== '' && Number(quantity) !== 0);
    if (wanted.length === 0) {
      setError(sale ? 'Say how many of at least one item came back.' : 'Say how many of at least one item go back.');
      return;
    }
    if (wanted.some(({ quantity }) => !QUANTITY.test(quantity))) {
      setError('A quantity is a number of the item’s unit, to at most four decimal places.');
      return;
    }
    if (date === '') {
      setError(sale ? 'Give the date the goods came back.' : 'Give the date the goods went back.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = sale
        ? await api.goodsReturn(entryId, { date, lines: wanted.map(({ line, quantity }) => ({ outLineId: line.lineId, quantity })) })
        : await api.vendorReturn(entryId, { date, lines: wanted.map(({ line, quantity }) => ({ billLineId: line.lineId, quantity })) });
      setRecorded(result.entry.headerId);
      setOpen(false);
      setAmounts({});
      load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not record the goods.');
    } finally {
      setBusy(false);
    }
  }

  const label = sale ? 'Record Returned Goods' : 'Send Goods Back';
  return (
    <section className="goods-return" aria-label={label}>
      {recorded !== null && (
        <Toast
          variant="ok"
          message={sale ? 'The returned goods are back in stock.' : 'The goods are recorded as sent back.'}
          onDismiss={() => setRecorded(null)}
        />
      )}
      {recorded !== null && (
        <p className="muted">
          <Link to={`/journals/${recorded}`}>{sale ? 'Open The Return' : 'Open The Vendor Credit'}</Link>
        </p>
      )}
      {!open && open_.length > 0 && (
        <button type="button" className="link" onClick={() => setOpen(true)}>
          {label}
        </button>
      )}
      {open && (
        <div className="goods-return-form">
          <p className="muted">
            {sale
              ? 'Goods a customer brought back go back into stock at what they cost when they left.'
              : 'Goods sent back come out of what this bill brought in, at what this bill paid for them.'}{' '}
            <InfoNote label={sale ? 'What A Return Records' : 'What Sending Back Records'}>
              {sale
                ? 'This records the goods only, on an entry of its own. Record the money as it happens: a refund on its bank transaction, or an entry reducing what the customer owes.'
                : 'This records a credit from the vendor against this bill, on an entry of its own, and matches it to what the bill still owes. If the bill is already paid, the credit waits for the vendor’s refund to be matched to it.'}
            </InfoNote>
          </p>
          <div className="table-scroll">
            <table className="lines" aria-label={sale ? 'Goods Sold' : 'Goods On This Bill'}>
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="num">{sale ? 'Sold' : 'Brought In'}</th>
                  <th className="num">{sale ? 'Not Yet Back' : 'Still On Hand'}</th>
                  <th className="num">{sale ? 'Coming Back' : 'Going Back'}</th>
                </tr>
              </thead>
              <tbody>
                {open_.map((line) => (
                  <tr key={line.lineId}>
                    <td>{line.item}</td>
                    <td className="num">
                      {showQuantity(line.quantity)} {line.unit}
                    </td>
                    <td className="num">
                      {showQuantity(line.left)} {line.unit}
                    </td>
                    <td className="num">
                      <input
                        aria-label={`${sale ? 'Coming Back' : 'Going Back'}: ${line.item}`}
                        className="line-quantity"
                        inputMode="decimal"
                        value={amounts[line.lineId] ?? ''}
                        onChange={(event) => {
                          const quantity = event.target.value;
                          setAmounts((current) => ({ ...current, [line.lineId]: quantity }));
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="row">
            <label>
              {sale ? 'Date Returned' : 'Date Sent Back'}{' '}
              <input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
            </label>
            <button type="button" disabled={busy} onClick={() => void submit()}>
              {sale ? 'Record The Return' : 'Send Them Back'}
            </button>
            <button
              type="button"
              className="link"
              onClick={() => {
                setOpen(false);
                setError(null);
              }}
            >
              Cancel
            </button>
          </div>
          {error && (
            <p role="alert" className="error">
              {error}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
